
import React, { useEffect, useRef, useState } from 'react';
import { X, BarChart3, Filter, CheckCircle, RefreshCw, Search, Layers, Calendar, CalendarDays, Zap, Clock, CalendarX, Info, Brain, Rocket, TrendingUp, Target, Plus, Minus, Home } from 'lucide-react';
import { Chart, registerables } from 'chart.js';
import { formatValue, perfColor, perfPct, getPerspectiveIcon } from '../utils/formatters';

Chart.register(...registerables);

const Dashboard = ({
  indicators,
  dashboardFilters,
  uniqueIndicatorsForDashboard,
  perspectives,
  indicatorTypes,
  searchTerm,
  setSearchTerm,
  selectedPerspective,
  setSelectedPerspective,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  togglePerspectiveFilter,
  toggleIndicatorFilter,
  selectAllFilters,
  clearAllFilters,
  setQuickPeriod,
  getDateRange,
  closeDashboard,
  openStrategicMap
}) => {
  const chartRefs = useRef({});
  const chartInstances = useRef({});
  const [localFilters, setLocalFilters] = useState({
    perspectives: [...perspectives],
    indicators: [...indicatorTypes],
  });

  // Função para navegação segura
  const handleNavigation = (navigationFn, label) => {
    try {
      console.log(`Executando navegação: ${label}`);
      if (typeof navigationFn === 'function') {
        navigationFn();
      } else {
        console.error(`Função de navegação inválida para: ${label}`);
      }
    } catch (error) {
      console.error(`Erro na navegação ${label}:`, error);
    }
  };

  // Função para voltar ao início
  const handleGoHome = () => {
    console.log('Navegando para a página inicial...');
    closeDashboard();
  };

  // Função para ir ao mapa estratégico
  const handleGoToStrategicMap = () => {
    console.log('Navegando para o Mapa Estratégico...');
    closeDashboard();
    setTimeout(() => {
      openStrategicMap();
    }, 100);
  };

  // Função para obter dados filtrados baseados nos filtros locais
  const getFilteredIndicators = () => {
    return indicators.filter(ind => {
      const matchesPerspective = localFilters.perspectives.includes(ind.perspective);
      const matchesIndicator = localFilters.indicators.includes(ind.indicator);

      // Filtros de data
      let matchesDate = true;
      if (startDate || endDate) {
        const indDate = new Date(ind.period);
        if (startDate) matchesDate = matchesDate && indDate >= new Date(startDate);
        if (endDate) matchesDate = matchesDate && indDate <= new Date(endDate);
      }

      // Busca por texto
      let matchesSearch = true;
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        matchesSearch = ind.indicator.toLowerCase().includes(term) ||
          ind.perspective.toLowerCase().includes(term) ||
          (ind.notes && ind.notes.toLowerCase().includes(term));
      }

      return matchesPerspective && matchesIndicator && matchesDate && matchesSearch;
    });
  };

  // Obter indicadores únicos filtrados
  const getUniqueFilteredIndicators = () => {
    const filtered = getFilteredIndicators();
    return [...new Set(filtered.map(ind => ind.indicator))];
  };

  const filteredIndicators = getFilteredIndicators();
  const uniqueFilteredIndicators = getUniqueFilteredIndicators();

  // Funções para manipular filtros locais
  const handleTogglePerspective = (perspective) => {
    setLocalFilters(prev => {
      const newPerspectives = prev.perspectives.includes(perspective)
        ? prev.perspectives.filter(p => p !== perspective)
        : [...prev.perspectives, perspective];
      return { ...prev, perspectives: newPerspectives };
    });
  };

  const handleToggleIndicator = (indicator) => {
    setLocalFilters(prev => {
      const newIndicators = prev.indicators.includes(indicator)
        ? prev.indicators.filter(i => i !== indicator)
        : [...prev.indicators, indicator];
      return { ...prev, indicators: newIndicators };
    });
  };

  const handleSelectAll = () => {
    setLocalFilters({
      perspectives: [...perspectives],
      indicators: [...indicatorTypes],
    });
  };

  const handleClearAll = () => {
    setLocalFilters({
      perspectives: [],
      indicators: [],
    });
    setSearchTerm("");
    setSelectedPerspective("All");
    setStartDate("");
    setEndDate("");
  };

  // Função para obter dados do gráfico
  const getChartData = (indicatorName) => {
    const data = indicators
      .filter(ind => ind.indicator === indicatorName)
      .sort((a, b) => new Date(a.period) - new Date(b.period))
      .map(ind => ({
        label: new Date(ind.period).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        realizado: parseFloat(ind.actual) || 0,
        meta: parseFloat(ind.target) || 0
      }));
    return data;
  };

  // Função para criar gráfico de barras
  const createBarChart = (canvas, data, label) => {
    if (!data || data.length === 0 || !canvas) return null;

    const ctx = canvas.getContext('2d');
    return new Chart(ctx, {
      type: 'bar',
      data: {
        labels: data.map(d => d.label),
        datasets: [
          {
            label: 'Realizado',
            data: data.map(d => d.realizado),
            backgroundColor: 'rgba(34, 197, 94, 0.8)',
            borderColor: 'rgba(34, 197, 94, 1)',
            borderWidth: 2,
            borderRadius: 4,
          },
          {
            label: 'Meta',
            data: data.map(d => d.meta),
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 2,
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 20,
              font: { size: 12, weight: '600' },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(0, 0, 0, 0.05)' },
          },
          x: {
            grid: { display: false },
          },
        },
      },
    });
  };

  // Função para criar gráfico de linha
  const createLineChart = (canvas, data, label) => {
    if (!data || data.length === 0 || !canvas) return null;

    const ctx = canvas.getContext('2d');
    return new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.label),
        datasets: [
          {
            label: 'Realizado',
            data: data.map(d => d.realizado),
            borderColor: 'rgba(34, 197, 94, 1)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            borderWidth: 3,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: 'rgba(34, 197, 94, 1)',
            pointBorderColor: 'white',
            pointBorderWidth: 2,
            pointRadius: 5,
          },
          {
            label: 'Meta',
            data: data.map(d => d.meta),
            borderColor: 'rgba(59, 130, 246, 1)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 2,
            borderDash: [8, 4],
            tension: 0.4,
            pointBackgroundColor: 'rgba(59, 130, 246, 1)',
            pointBorderColor: 'white',
            pointBorderWidth: 2,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 20,
              font: { size: 12, weight: '600' },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(0, 0, 0, 0.05)' },
          },
          x: {
            grid: { display: false },
          },
        },
      },
    });
  };

  // Effect para renderizar gráficos
  useEffect(() => {
    const renderCharts = async () => {
      try {
        // Destruir gráficos existentes
        Object.values(chartInstances.current).forEach(chart => {
          if (chart && typeof chart.destroy === 'function') {
            try {
              chart.destroy();
            } catch (error) {
              console.warn('Erro ao destruir gráfico:', error);
            }
          }
        });
        chartInstances.current = {};

        // Criar novos gráficos para os indicadores selecionados
        for (let i = 0; i < uniqueFilteredIndicators.length; i++) {
          const indicator = uniqueFilteredIndicators[i];
          const canvas = chartRefs.current[indicator];

          if (!canvas) continue;

          const data = getChartData(indicator);
          if (data && data.length > 0) {
            try {
              // Alternar entre gráfico de barras e linha baseado no índice
              if (i % 2 === 0) {
                chartInstances.current[indicator] = createBarChart(canvas, data, indicator);
              } else {
                chartInstances.current[indicator] = createLineChart(canvas, data, indicator);
              }
            } catch (error) {
              console.error(`Erro ao criar gráfico para ${indicator}:`, error);
            }
          }
        }
      } catch (error) {
        console.error('Erro geral ao renderizar gráficos:', error);
      }
    };

    renderCharts().catch(error => {
      console.error('Erro não capturado ao renderizar gráficos:', error);
    });

    return () => {
      Object.values(chartInstances.current).forEach(chart => {
        if (chart && typeof chart.destroy === 'function') {
          try {
            chart.destroy();
          } catch (error) {
            console.warn('Erro ao destruir gráfico no cleanup:', error);
          }
        }
      });
    };
  }, [uniqueFilteredIndicators, indicators, startDate, endDate, searchTerm]);

  const dateRange = getDateRange();

  const PerspectiveIcon = ({ perspective }) => {
    const iconName = getPerspectiveIcon(perspective);
    if (iconName === 'dollar-sign') return <span>💰</span>;
    if (iconName === 'users') return <span>👥</span>;
    return <span>📊</span>;
  };

  return (
    <div className="fixed inset-0 bg-gray-50 z-40 overflow-y-auto">
      {/* Header do Dashboard */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-50">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={handleGoHome}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors text-gray-700 font-medium"
            >
              <X className="w-5 h-5" />
              Voltar
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard Analytics</h1>
                <p className="text-gray-600 text-sm">Visão Analítica dos Indicadores BSC</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition-colors text-white font-medium"
            >
              <Home className="w-4 h-4" />
              Início
            </button>
            <button
              onClick={handleGoToStrategicMap}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition-colors text-white font-medium"
            >
              <Target className="w-4 h-4" />
              Mapa Estratégico
            </button>
            <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-600">Indicadores Ativos</div>
              <div className="text-xl font-bold text-blue-700">{uniqueFilteredIndicators.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Filtros */}
        <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtros Avançados
            </h3>
            <div className="flex gap-3">
              <button
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                onClick={handleSelectAll}
              >
                <CheckCircle className="w-4 h-4" />
                Selecionar Todos
              </button>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-all"
                onClick={handleClearAll}
              >
                <RefreshCw className="w-4 h-4" />
                Limpar
              </button>
            </div>
          </div>

          {/* Filtros de Busca e Data */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
              <input
                type="text"
                placeholder="Buscar indicadores..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Inicial</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={dateRange.min}
                max={dateRange.max}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Final</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={dateRange.min}
                max={dateRange.max}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Período Rápido</label>
              <select
                onChange={(e) => setQuickPeriod(parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="0">Todos os períodos</option>
                <option value="3">Últimos 3 meses</option>
                <option value="6">Últimos 6 meses</option>
                <option value="12">Último ano</option>
              </select>
            </div>
          </div>

          {/* Filtros de Perspectiva */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Perspectivas</label>
            <div className="flex flex-wrap gap-2">
              {perspectives.map(perspective => (
                <button
                  key={perspective}
                  onClick={() => handleTogglePerspective(perspective)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border-2 ${localFilters.perspectives.includes(perspective)
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  <PerspectiveIcon perspective={perspective} />
                  {perspective}
                  {localFilters.perspectives.includes(perspective) ? (
                    <Minus className="w-3 h-3" />
                  ) : (
                    <Plus className="w-3 h-3" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Filtros de Indicadores */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Indicadores ({localFilters.indicators.length}/{indicatorTypes.length})
            </label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-lg">
              {indicatorTypes.map(indicator => (
                <button
                  key={indicator}
                  onClick={() => handleToggleIndicator(indicator)}
                  className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-all border ${localFilters.indicators.includes(indicator)
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  {indicator}
                  {localFilters.indicators.includes(indicator) ? (
                    <Minus className="w-3 h-3" />
                  ) : (
                    <Plus className="w-3 h-3" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Info dos Resultados */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-blue-700">
                <Info className="w-4 h-4 inline mr-1" />
                Mostrando {filteredIndicators.length} registros de {uniqueFilteredIndicators.length} indicadores únicos
              </span>
              <span className="text-blue-600">
                {localFilters.perspectives.length} perspectiva(s) | {localFilters.indicators.length} indicador(es)
              </span>
            </div>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="grid grid-cols-12 gap-6">
          {/* Gráficos */}
          <div className="col-span-12 lg:col-span-8">
            {uniqueFilteredIndicators.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center shadow-lg">
                <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-3">
                  Nenhum indicador selecionado
                </h3>
                <p className="text-gray-500">
                  Selecione pelo menos uma perspectiva e um indicador para visualizar os gráficos.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {uniqueFilteredIndicators.map((indicator) => {
                  const latestData = indicators
                    .filter(ind => ind.indicator === indicator)
                    .sort((a, b) => new Date(b.period) - new Date(a.period))[0];

                  return (
                    <div key={indicator} className="bg-white rounded-xl p-6 shadow-lg">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-800">{indicator}</h4>
                          <p className="text-sm text-gray-600">{latestData?.perspective}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900">
                            {formatValue(latestData?.actual, latestData?.unit)}
                          </div>
                          <div className="text-sm text-gray-600">
                            Meta: {formatValue(latestData?.target, latestData?.unit)}
                          </div>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${perfColor(latestData?.actual, latestData?.target)}`}>
                            {perfPct(latestData?.actual, latestData?.target)}%
                          </span>
                        </div>
                      </div>
                      <div className="h-64">
                        <canvas
                          ref={(el) => {
                            if (el) chartRefs.current[indicator] = el;
                          }}
                          key={`chart-${indicator}`}
                        ></canvas>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            {/* Resumo de Performance */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-blue-600 text-white p-4">
                <h3 className="font-bold text-center flex items-center justify-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Resumo de Performance
                </h3>
              </div>
              <div className="p-4 max-h-96 overflow-y-auto">
                {uniqueFilteredIndicators.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <Target className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    Selecione indicadores para ver o resumo
                  </div>
                ) : (
                  <div className="space-y-3">
                    {uniqueFilteredIndicators.map(indicator => {
                      const latest = indicators
                        .filter(ind => ind.indicator === indicator)
                        .sort((a, b) => new Date(b.period) - new Date(a.period))[0];

                      return (
                        <div key={indicator} className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-800 text-sm">{indicator}</span>
                            <span className="text-lg font-bold text-gray-900">
                              {formatValue(latest?.actual, latest?.unit)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">
                              Meta: {formatValue(latest?.target, latest?.unit)}
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${perfColor(latest?.actual, latest?.target)}`}>
                              {perfPct(latest?.actual, latest?.target)}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Análise Rápida */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-green-600 text-white p-4">
                <h3 className="font-bold text-center flex items-center justify-center gap-2">
                  <Brain className="w-5 h-5" />
                  Análise Rápida
                </h3>
              </div>
              <div className="p-4 text-sm max-h-64 overflow-y-auto">
                {uniqueFilteredIndicators.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <Brain className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    Análises aparecerão aqui
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">Destaques Positivos:</h4>
                      <p className="text-green-700">
                        {uniqueFilteredIndicators.length} indicadores sendo monitorados ativamente.
                      </p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">Recomendação:</h4>
                      <p className="text-blue-700">
                        Continue monitorando os indicadores selecionados para identificar tendências.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
